import React, { useState } from "react";
import { Icon } from "../icons";
import { fmtDate, todayStr } from "../lib/format";
import { useMcOps } from "../lib/McOpsContext";

const MAX_RESULTS = 8;

/** Same matching as the header job search: Zoho #, client name or phone. */
const matchJobs = (jobs, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return jobs
    .filter(
      (j) =>
        (j.zohoId || "").toLowerCase().includes(q) ||
        (j.clientName || "").toLowerCase().includes(q) ||
        (j.phone || "").toLowerCase().includes(q),
    )
    .slice(0, MAX_RESULTS);
};

/**
 * Job picker for a reminder: type a Zoho #, name or phone, pick from the
 * matches; the pick shows as a chip with a remove button. Nothing picked means
 * a general reminder.
 */
const JobPicker = ({ jobs, jobId, onPick }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const picked = jobId ? jobs.find((j) => j.id === jobId) : null;

  if (picked) {
    return (
      <div className="rem-job-chip">
        <Icon name="clipboard" />
        <span className="rem-job-chip-label">
          {picked.zohoId} · {picked.clientName}
        </span>
        <button
          type="button"
          className="rem-job-chip-x"
          aria-label="Remove job"
          title="General reminder (no job)"
          onClick={() => onPick("")}
        >
          <Icon name="x" />
        </button>
      </div>
    );
  }

  const list = matchJobs(jobs, query);
  return (
    <div
      className="job-search-wrap rem-job-search"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <div className="job-search-box">
        <Icon name="search" />
        <input
          type="text"
          placeholder="Search job by Zoho job #"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
      </div>
      <div className={`job-search-results${open && list.length ? " open" : ""}`}>
        {list.map((j) => (
          <div
            className="search-result"
            key={j.id}
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onPick(j.id);
              setQuery("");
              setOpen(false);
            }}
          >
            <div>
              <div className="sr-name">{j.clientName}</div>
              <div className="sr-meta">
                {j.zohoId}
                {j.moveDate ? " · " + fmtDate(j.moveDate) : ""}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * The "New reminder" form shared by the Reminders hub, the Reminders page and
 * the landing-page panels: searchable job, date, optional time, text.
 */
export const ReminderForm = ({ defaultDate, style, compact = false }) => {
  const { state, addReminder } = useMcOps();
  const [jobId, setJobId] = useState("");
  const [date, setDate] = useState(defaultDate || todayStr());
  const [time, setTime] = useState("");
  const [text, setText] = useState("");

  const save = () => {
    if (!text.trim()) return;
    addReminder({ jobId, date: date || todayStr(), time, text: text.trim() });
    setText("");
  };

  return (
    <div className="reminder-form" style={style}>
      <div className="field-row">
        <JobPicker jobs={state.jobs} jobId={jobId} onPick={setJobId} />
        <div className="reminder-when">
          <input
            type="date"
            aria-label="Reminder date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <input
            type="time"
            aria-label="Reminder time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </div>
      </div>
      <textarea
        placeholder="Reminder text..."
        rows={2}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="toolbar-row" style={{ marginBottom: compact ? 0 : 6 }}>
        <button className="btn primary small" type="button" onClick={save}>
          <Icon name="bell" />
          Save reminder
        </button>
      </div>
    </div>
  );
};
